import type {
  BootstrapResponse,
  Customer,
  CustomerDetail,
  CustomerPatch,
  CustomerRisk,
  DeliveryOption,
  FinanceCategory,
  FinanceEntry,
  FinanceSummary,
  NotificationPrefs,
  Order,
  OrderCreateInput,
  OrderDetail,
  OrderPaymentSummary,
  OrderStatus,
  OrderUpdateInput,
  Page,
  PaymentLink,
  ProductSuggestion,
  SalesStats,
} from '@multicrm/shared';
import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import { api } from './api';

/** Ключи кэша в одном месте: иначе инвалидация со временем начинает врать. */
export const queryKeys = {
  bootstrap: ['bootstrap'] as const,
  orders: (filters: OrderFilters) => ['orders', 'list', filters] as const,
  orderCounts: (stores: string[], status: OrderStatus) =>
    ['orders', 'count', stores, status] as const,
  order: (ref: string) => ['order', ref] as const,
  orderPayments: (ref: string) => ['order', ref, 'payments'] as const,
  customers: (filters: CustomerFilters) => ['customers', filters] as const,
  customer: (ref: string) => ['customer', ref] as const,
  financeSummary: (storeId: string, month?: string) =>
    ['finance', 'summary', storeId, month ?? 'all'] as const,
  financeEntries: (storeId: string, month?: string) =>
    ['finance', 'entries', storeId, month ?? 'all'] as const,
  financeCategories: (storeId: string) => ['finance', 'categories', storeId] as const,
  stats: (stores: string[], period: string) => ['stats', stores, period] as const,
  products: (storeId: string, query: string) => ['products', storeId, query] as const,
  cities: (storeId: string, carrier: string, query: string) =>
    ['delivery', 'cities', storeId, carrier, query] as const,
  branches: (storeId: string, carrier: string, cityRef: string, query: string) =>
    ['delivery', 'branches', storeId, carrier, cityRef, query] as const,
  notifications: (chatId?: number) => ['settings', 'notifications', chatId ?? 'me'] as const,
  sessions: ['settings', 'sessions'] as const,
  sync: ['settings', 'sync'] as const,
};

export interface OrderFilters {
  stores: string[];
  status: OrderStatus[];
  search: string;
}

export interface CustomerFilters {
  stores: string[];
  search: string;
  nonPickup: boolean;
}

const PAGE_SIZE = 20;

function listParams(filters: OrderFilters, page: number) {
  return {
    page,
    limit: PAGE_SIZE,
    stores: filters.stores.length > 0 ? filters.stores.join(',') : undefined,
    status: filters.status.length > 0 ? filters.status.join(',') : undefined,
    search: filters.search.trim().length >= 2 ? filters.search.trim() : undefined,
  };
}

export function useBootstrap() {
  return useQuery({
    queryKey: queryKeys.bootstrap,
    queryFn: () => api<BootstrapResponse>('/api/me'),
    // Состав магазинов и права меняются редко — лишние запросы не нужны.
    staleTime: 5 * 60_000,
  });
}

export function useOrders(filters: OrderFilters) {
  return useInfiniteQuery({
    queryKey: queryKeys.orders(filters),
    initialPageParam: 1,
    queryFn: ({ pageParam, signal }) =>
      api<Page<Order>>('/api/orders', { query: listParams(filters, pageParam), signal }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.page + 1 : undefined),
    // Список заказов стареет быстро: при возврате на экран обновляем.
    staleTime: 15_000,
  });
}

/**
 * Количество заказов по каждому статусу для бейджей на вкладках: берём `total`
 * из выдачи с limit=1, поэтому это один лёгкий запрос на статус.
 */
export function useOrderStatusCounts(stores: string[], statuses: OrderStatus[]) {
  const results = useQueries({
    queries: statuses.map((status) => ({
      queryKey: queryKeys.orderCounts(stores, status),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        api<Page<Order>>('/api/orders', {
          query: {
            page: 1,
            limit: 1,
            stores: stores.length > 0 ? stores.join(',') : undefined,
            status,
          },
          signal,
        }),
      staleTime: 15_000,
    })),
  });

  const counts: Partial<Record<OrderStatus, number>> = {};
  statuses.forEach((status, index) => {
    const total = results[index]?.data?.total;
    if (total !== undefined) counts[status] = total;
  });
  const ready = results.every((result) => !result.isPending);
  return { counts, ready };
}

export function useOrder(ref: string | undefined) {
  return useQuery({
    queryKey: queryKeys.order(ref ?? ''),
    queryFn: () => api<OrderDetail>(`/api/orders/${encodeURIComponent(ref ?? '')}`),
    enabled: Boolean(ref),
    staleTime: 10_000,
  });
}

export interface StatusChangeInput {
  ref: string;
  status: OrderStatus;
  expectedStatus: OrderStatus;
}

/**
 * Смена статуса с оптимистичным обновлением: карточка меняется сразу,
 * запрос уходит в фоне. Если магазин ответит ошибкой — откатываем и показываем
 * причину, а не оставляем интерфейс врать.
 */
export function useChangeOrderStatus(): UseMutationResult<Order, Error, StatusChangeInput> {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ ref, status, expectedStatus }: StatusChangeInput) =>
      api<Order>(`/api/orders/${encodeURIComponent(ref)}/status`, {
        method: 'PATCH',
        body: { status, expectedStatus, silent: false },
      }),

    onMutate: async ({ ref, status }) => {
      await client.cancelQueries({ queryKey: queryKeys.order(ref) });
      const previousDetail = client.getQueryData<OrderDetail>(queryKeys.order(ref));

      if (previousDetail) {
        client.setQueryData<OrderDetail>(queryKeys.order(ref), { ...previousDetail, status });
      }
      patchOrderInLists(client, ref, (order) => ({ ...order, status }));

      return { previousDetail };
    },

    onError: (_error, { ref }, context) => {
      if (context?.previousDetail) {
        client.setQueryData(queryKeys.order(ref), context.previousDetail);
      }
      void client.invalidateQueries({ queryKey: ['orders'] });
    },

    onSuccess: (order) => {
      client.setQueryData<OrderDetail>(queryKeys.order(order.ref), (current) =>
        current ? { ...current, ...order } : current,
      );
      patchOrderInLists(client, order.ref, () => order);
    },

    onSettled: (_data, _error, { ref }) => {
      void client.invalidateQueries({ queryKey: queryKeys.order(ref) });
      void client.invalidateQueries({ queryKey: ['orders'] });
      void client.invalidateQueries({ queryKey: ['stats'] });
    },
  });
}

type QueryClientLike = ReturnType<typeof useQueryClient>;

/** Пройтись по всем страницам всех списков заказов и подменить один заказ. */
export function patchOrderInLists(
  client: QueryClientLike,
  ref: string,
  update: (order: Order) => Order,
): void {
  client.setQueriesData<{ pages: Array<Page<Order>>; pageParams: unknown[] }>(
    { queryKey: ['orders', 'list'] },
    (data) => {
      if (!data || !Array.isArray(data.pages)) return data;
      return {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          items: Array.isArray(page?.items)
            ? page.items.map((order) => (order.ref === ref ? update(order) : order))
            : page?.items ?? [],
        })),
      };
    },
  );
}

/**
 * Запись номера накладной. Отдельная мутация, а не часть смены статуса:
 * в CRM это разные действия, и менеджер обычно сначала сканирует ТТН,
 * а статус меняет потом.
 */
export function useSetTrackingNumber(ref: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (trackingNumber: string) =>
      api<Order>(`/api/orders/${encodeURIComponent(ref)}/tracking`, {
        method: 'PATCH',
        body: { trackingNumber },
      }),
    onSuccess: (order) => {
      client.setQueryData<OrderDetail>(queryKeys.order(ref), (current) =>
        current ? { ...current, ...order } : current,
      );
      patchOrderInLists(client, ref, () => order);
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: queryKeys.order(ref) });
      void client.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

/**
 * Создание заказа. После успеха сбрасываем списки и сводку: заказ должен
 * появиться там сразу, не дожидаясь ни опроса магазина, ни события по SSE.
 */
export function useCreateOrder() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: OrderCreateInput) =>
      api<Order>('/api/orders', { method: 'POST', body: input }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['orders'] });
      void client.invalidateQueries({ queryKey: ['stats'] });
    },
  });
}

export function useUpdateOrder(ref: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: OrderUpdateInput) =>
      api<Order>(`/api/orders/${encodeURIComponent(ref)}`, { method: 'PATCH', body: input }),
    onSuccess: (order) => {
      client.setQueryData<OrderDetail>(queryKeys.order(ref), (current) =>
        current ? { ...current, ...order } : current,
      );
      patchOrderInLists(client, ref, () => order);
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: queryKeys.order(ref) });
      void client.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

/** Произвольная SMS клиенту по заказу. Телефон берёт сам gateway из заказа. */
export function useSendOrderSms(ref: string) {
  return useMutation({
    mutationFn: (message: string) =>
      api<{ sent: boolean }>(`/api/orders/${encodeURIComponent(ref)}/sms`, {
        method: 'POST',
        body: { message },
      }),
  });
}

/**
 * Проверка клиента на надёжность. Запрашивается только когда телефон уже
 * похож на телефон: иначе на каждый введённый символ летел бы запрос.
 */
export function useCustomerRisk(storeId: string | undefined, phone: string, enabled: boolean) {
  return useQuery({
    queryKey: ['customers', 'risk', storeId ?? '', phone],
    queryFn: () => api<CustomerRisk>('/api/customers/risk', { query: { storeId, phone } }),
    enabled: enabled && Boolean(storeId) && phone.replace(/\D+/g, '').length >= 9,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useUpdateStock(storeId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: { productId: string; quantity: number; inStock?: boolean }) =>
      api<{ ok: boolean }>(`/api/catalog/products/${encodeURIComponent(input.productId)}/stock`, {
        method: 'POST',
        body: { storeId, quantity: input.quantity, inStock: input.inStock },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useOrderPayments(ref: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.orderPayments(ref ?? ''),
    queryFn: () =>
      api<OrderPaymentSummary>(`/api/orders/${encodeURIComponent(ref ?? '')}/payment-links`),
    enabled: Boolean(ref) && enabled,
  });
}

export function useCreatePaymentLink(ref: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (amount: number) =>
      api<PaymentLink>(`/api/orders/${encodeURIComponent(ref)}/payment-links`, {
        method: 'POST',
        body: { amount },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.orderPayments(ref) });
      void client.invalidateQueries({ queryKey: queryKeys.order(ref) });
    },
  });
}

export function useCustomers(filters: CustomerFilters) {
  return useInfiniteQuery({
    queryKey: queryKeys.customers(filters),
    initialPageParam: 1,
    queryFn: ({ pageParam, signal }) =>
      api<Page<Customer>>('/api/customers', {
        query: {
          page: pageParam,
          limit: PAGE_SIZE,
          stores: filters.stores.length > 0 ? filters.stores.join(',') : undefined,
          search: filters.search.trim().length >= 2 ? filters.search.trim() : undefined,
          nonPickup: filters.nonPickup ? 'true' : undefined,
        },
        signal,
      }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.page + 1 : undefined),
    staleTime: 60_000,
  });
}

export function useCustomer(ref: string | undefined) {
  return useQuery({
    queryKey: queryKeys.customer(ref ?? ''),
    queryFn: () => api<CustomerDetail>(`/api/customers/${encodeURIComponent(ref ?? '')}`),
    enabled: Boolean(ref),
  });
}

export function usePatchCustomer(ref: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (patch: CustomerPatch) =>
      api<Customer>(`/api/customers/${encodeURIComponent(ref)}`, { method: 'PATCH', body: patch }),
    onSuccess: (customer) => {
      client.setQueryData<CustomerDetail>(queryKeys.customer(ref), (current) =>
        current ? { ...current, ...customer } : current,
      );
      void client.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

export function useFinanceSummary(storeId: string | undefined, month?: string) {
  return useQuery({
    queryKey: queryKeys.financeSummary(storeId ?? '', month),
    queryFn: () => api<FinanceSummary>('/api/finance/summary', { query: { storeId, month } }),
    enabled: Boolean(storeId),
    staleTime: 60_000,
  });
}

export function useFinanceEntries(storeId: string | undefined, month?: string) {
  return useQuery({
    queryKey: queryKeys.financeEntries(storeId ?? '', month),
    queryFn: () =>
      api<Page<FinanceEntry>>('/api/finance/entries', {
        query: { storeId, month, limit: 50, page: 1 },
      }),
    enabled: Boolean(storeId),
    staleTime: 60_000,
  });
}

export function useFinanceCategories(storeId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.financeCategories(storeId ?? ''),
    queryFn: () => api<FinanceCategory[]>('/api/finance/categories', { query: { storeId } }),
    enabled: Boolean(storeId),
    staleTime: 10 * 60_000,
  });
}

export function useCreateFinanceEntry(storeId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      type: 'income' | 'expense';
      categoryId: string;
      amount: number;
      entryDate: string;
      note?: string;
    }) =>
      api<FinanceEntry>('/api/finance/entries', { method: 'POST', body: { ...input, storeId } }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['finance'] });
    },
  });
}

export function useStats(stores: string[], period: string) {
  return useQuery({
    queryKey: queryKeys.stats(stores, period),
    queryFn: () =>
      api<SalesStats>('/api/stats', {
        query: { stores: stores.length > 0 ? stores.join(',') : undefined, period },
      }),
    staleTime: 60_000,
  });
}

export function useProductSearch(storeId: string | undefined, query: string) {
  return useQuery({
    queryKey: queryKeys.products(storeId ?? '', query),
    queryFn: () =>
      api<{ items: ProductSuggestion[] }>('/api/catalog/products', {
        query: { storeId, q: query, limit: 50 },
      }),
    enabled: Boolean(storeId) && query.trim().length >= 2,
    staleTime: 60_000,
  });
}

export function useDeliveryCities(storeId: string | undefined, carrier: string, query: string) {
  return useQuery({
    queryKey: queryKeys.cities(storeId ?? '', carrier, query),
    queryFn: () =>
      api<{ items: DeliveryOption[] }>('/api/catalog/delivery/cities', {
        query: { storeId, carrier, q: query },
      }),
    enabled: Boolean(storeId) && query.trim().length >= 2,
    staleTime: 10 * 60_000,
  });
}

export function useDeliveryBranches(
  storeId: string | undefined,
  carrier: string,
  cityRef: string,
  query: string,
) {
  return useQuery({
    queryKey: queryKeys.branches(storeId ?? '', carrier, cityRef, query),
    queryFn: () =>
      api<{ items: DeliveryOption[] }>('/api/catalog/delivery/branches', {
        query: { storeId, carrier, cityRef, q: query || undefined },
      }),
    enabled: Boolean(storeId) && cityRef.length > 0,
    staleTime: 10 * 60_000,
  });
}

export function useNotificationPrefs(chatId?: number) {
  return useQuery({
    queryKey: queryKeys.notifications(chatId),
    queryFn: () =>
      api<NotificationPrefs>('/api/settings/notifications', {
        query: chatId ? { chatId } : undefined,
      }),
    staleTime: 60_000,
  });
}

export function useSaveNotificationPrefs(chatId?: number) {
  const client = useQueryClient();
  const key = queryKeys.notifications(chatId);
  return useMutation({
    mutationFn: (prefs: NotificationPrefs) =>
      api<NotificationPrefs>('/api/settings/notifications', {
        method: 'PUT',
        query: chatId ? { chatId } : undefined,
        body: prefs,
      }),
    onMutate: (prefs) => {
      // Переключатель должен срабатывать мгновенно.
      const previous = client.getQueryData<NotificationPrefs>(key);
      client.setQueryData(key, prefs);
      return { previous };
    },
    onError: (_error, _prefs, context) => {
      if (context?.previous) client.setQueryData(key, context.previous);
    },
  });
}

export interface CrmUserItem {
  telegramId: number;
  role: 'owner' | 'manager' | 'courier' | 'viewer';
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  title?: string | null;
  photoUrl: string | null;
  isGroup?: boolean;
  type?: 'group' | 'user';
  botActive?: boolean;
  lastSeenAt: number | null;
  activeSessions: number;
  isOnline: boolean;
}

export function useCrmUsers() {
  return useQuery({
    queryKey: ['settings', 'users'] as const,
    queryFn: () => api<{ users: CrmUserItem[] }>('/api/settings/users'),
  });
}

export function useSessions() {
  return useQuery({
    queryKey: queryKeys.sessions,
    queryFn: () =>
      api<{
        current: string;
        sessions: Array<{ sid: string; createdAt: number; lastSeenAt: number }>;
      }>('/api/settings/sessions'),
  });
}

export function useRevokeOtherSessions() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api<{ revoked: number }>('/api/settings/sessions', { method: 'DELETE' }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.sessions });
    },
  });
}

export function useSyncStatus(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.sync,
    queryFn: () =>
      api<{
        pollIntervalSec: number;
        enabled: boolean;
        stores: Array<{
          storeId: string;
          cursor: string | null;
          seeded: boolean;
          lastRunAt: string | null;
        }>;
      }>('/api/settings/sync'),
    enabled,
    staleTime: 30_000,
  });
}
